'use client';

import type { ReactNode } from 'react';
import { useThemePrefsStore, type HeaderStyle } from '@/shared/stores/theme-prefs-store';
import { PinPageButton } from '@/shared/components/layout/pin-page-button';
import { BackButton } from '@/shared/components/layout/back-button';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  className?: string;
  /** Override stilul ales din Setări (util pentru preview) */
  styleOverride?: HeaderStyle;
  /** Hide Pin button (pentru mockup-uri / live previews care folosesc PageHeader fără styleOverride). */
  noPin?: boolean;
  /** Show a back arrow button before the title that calls router.back() on click. */
  showBack?: boolean;
}

const GRADIENT_RADIAL =
  'radial-gradient(ellipse 110% 450% at 0% 0%, rgb(var(--primary-600)) 0%, rgb(var(--primary-500)) 16%, rgb(var(--primary-400)) 33%, rgb(var(--primary-200)) 50%, #FFFFFF 72%)';

const GRADIENT_LINEAR =
  'linear-gradient(to right, rgb(var(--primary-500)) 0%, rgb(var(--primary-400)) 25%, rgb(var(--primary-200)) 55%, #FFFFFF 85%)';

export function PageHeader({ title, subtitle, actions, className = '', styleOverride, noPin, showBack }: PageHeaderProps) {
  const storeStyle = useThemePrefsStore((s) => s.headerStyle);
  const style = styleOverride ?? storeStyle;
  // Pin button SKIP în 2 cazuri:
  //  - styleOverride setat (preview cards Setări→Aspect — nested în <button>,
  //    ar cauza hydration error)
  //  - noPin explicit (live preview Facturi din Setări→Aspect, mockup-uri)
  const isPreview = styleOverride !== undefined || noPin === true;

  // Shared outer wrapper classes.
  // Mobile (default): stack vertical — title on top, actions below in horizontal scroll row.
  // Desktop (md+): horizontal layout with actions right-aligned.
  // Padding vertical: redus când nu există subtitle (compactare → mai mult spațiu pt. KPI cards above-the-fold).
  const padY = subtitle ? 'py-2.5' : 'py-1.5';
  // Înălțime minimă consecventă: paginile fără acțiuni/subtitle (ex. Reconciliere,
  // Trezorerie) ar avea altfel bara titlului mult mai joasă decât cele cu controale
  // de 42px. min-h-[54px] (= 42px control + py-1.5) le aliniază la aceeași înălțime;
  // paginile cu controale sau subtitle depășesc deja pragul → neafectate.
  const baseCls =
    `flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 md:flex-wrap px-3 md:px-4 ${padY} min-h-[54px] border-b border-neutral-100 dark:border-neutral-800`;
  const actionsCls =
    'flex items-center gap-2 shrink-0 overflow-x-auto md:overflow-visible -mx-3 md:mx-0 px-3 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

  if (style === 'minimal') {
    return (
      <div className={baseCls + ' ' + className}>
        <div className="min-w-0 flex items-center gap-2">
          {showBack && <BackButton variant="light" />}
          <MinimalTitle title={title} subtitle={subtitle} />
          {!isPreview && <PinPageButton variant="light" />}
        </div>
        {actions && <div className={actionsCls}>{actions}</div>}
      </div>
    );
  }

  if (style === 'clean') {
    return (
      <div className={baseCls + ' ' + className}>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {showBack && <BackButton variant="light" />}
            <h1 className="text-[1.375rem] font-semibold text-neutral-900 dark:text-neutral-100 leading-tight">
              {title}
            </h1>
            {!isPreview && <PinPageButton variant="light" />}
          </div>
          {subtitle && <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{subtitle}</p>}
        </div>
        {actions && <div className={actionsCls}>{actions}</div>}
      </div>
    );
  }

  if (style === 'outline') {
    return (
      <div className={baseCls + ' ' + className}>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {showBack && <BackButton variant="light" />}
            <h1 className="text-[1.375rem] font-bold text-neutral-900 dark:text-neutral-100 leading-tight uppercase tracking-wide">
              {title}
            </h1>
            {!isPreview && <PinPageButton variant="light" />}
          </div>
          <div className="h-[3px] w-10 bg-primary rounded-full mt-1" />
          {subtitle && <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{subtitle}</p>}
        </div>
        {actions && <div className={actionsCls}>{actions}</div>}
      </div>
    );
  }

  if (style === 'solid-bar') {
    return (
      <div
        className={baseCls + ' rounded-tl-xl bg-primary ' + className}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {showBack && <BackButton variant="dark" />}
            <h1 className="text-[1.375rem] font-bold text-white leading-tight uppercase tracking-wide" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
              {title}
            </h1>
            {!isPreview && <PinPageButton variant="dark" />}
          </div>
          {subtitle && (
            <p className="mt-1 text-sm text-white/90" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
              {subtitle}
            </p>
          )}
        </div>
        {actions && <div className={actionsCls}>{actions}</div>}
      </div>
    );
  }

  // gradient-linear, or gradient-radial as the fall-through for any other value
  // (the new-user default is 'minimal', handled in its own branch above)
  const bg = style === 'gradient-linear' ? GRADIENT_LINEAR : GRADIENT_RADIAL;
  return (
    <div
      className={baseCls + ' rounded-tl-xl ' + className}
      style={{ background: bg }}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {showBack && <BackButton variant="dark" />}
          <h1
            className="relative inline-block text-[1.375rem] font-bold text-white leading-tight pb-1 uppercase tracking-wide after:absolute after:left-0 after:bottom-0 after:h-[3px] after:w-3/4 after:bg-white after:rounded-full before:absolute before:left-3/4 before:bottom-[1px] before:h-[1px] before:w-1/4 before:bg-white"
            style={{ textShadow: '0 1px 2px rgba(0,0,0,0.08)' }}
          >
            {title}
          </h1>
          {!isPreview && <PinPageButton variant="dark" />}
        </div>
        {subtitle && (
          <p className="mt-1 text-sm text-white/90" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div className={actionsCls}>{actions}</div>}
    </div>
  );
}

function MinimalTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="min-w-0">
      <h1 className="relative inline-block text-[1.375rem] font-bold text-primary leading-tight pb-1 uppercase tracking-wide after:absolute after:left-0 after:bottom-0 after:h-[3px] after:w-3/4 after:bg-primary after:rounded-full before:absolute before:left-3/4 before:bottom-[1px] before:h-[1px] before:w-1/4 before:bg-primary">
        {title}
      </h1>
      {subtitle && <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{subtitle}</p>}
    </div>
  );
}

